import { motion } from 'framer-motion'

interface EnrichmentActionsProps {
  query: string
  profileData: any
  callerIdData?: any
  onRerunSearch?: (query: string, forceRefresh: boolean) => Promise<void>
}

interface ActionItem {
  label: string
  description: string
  icon: string
  color: string
  query: string
  available: boolean
}

export function EnrichmentActions({ query, profileData, callerIdData, onRerunSearch }: EnrichmentActionsProps) {
  const actions: ActionItem[] = []

  // Discover new emails from breach data that differ from the query
  const discoveredEmails = new Set<string>()
  profileData?.breachSources?.forEach((breach: any) => {
    if (breach.email && breach.email !== query && breach.email.includes('@')) {
      discoveredEmails.add(breach.email)
    }
    if (breach.allRecords) {
      breach.allRecords.forEach((r: any) => {
        const email = r.Email || r.email || r.EmailAddress
        if (email && email !== query && email.includes('@')) {
          discoveredEmails.add(email)
        }
      })
    }
  })

  // Discover phone numbers
  const discoveredPhones = new Set<string>()
  profileData?.breachSources?.forEach((breach: any) => {
    const phone = breach.phone || breach.phoneNumber
    if (phone && phone.length >= 10) discoveredPhones.add(phone)
    if (breach.allRecords) {
      breach.allRecords.forEach((r: any) => {
        const ph = r.Phone || r.phone || r.PhoneNumber
        if (ph && ph.length >= 10) discoveredPhones.add(ph)
      })
    }
  })

  // Discover domains
  const discoveredDomains = new Set<string>()
  if (query.includes('@')) {
    discoveredDomains.add(query.split('@')[1])
  }
  discoveredEmails.forEach(email => {
    if (email.includes('@')) discoveredDomains.add(email.split('@')[1])
  })

  // Discover names
  const discoveredNames = new Set<string>()
  if (profileData?.personalInfo?.fullName) discoveredNames.add(profileData.personalInfo.fullName)
  profileData?.breachSources?.forEach((breach: any) => {
    if (breach.fullName && breach.fullName.length > 3) discoveredNames.add(breach.fullName)
  })

  // Build action items for discovered entities
  discoveredEmails.forEach(email => {
    actions.push({
      label: `Investigate ${email.length > 25 ? email.slice(0, 22) + '...' : email}`,
      description: 'Run full breach search on this discovered email',
      icon: '📧',
      color: 'border-blue-500/30 hover:border-blue-500/60 bg-blue-900/10',
      query: email,
      available: true,
    })
  })

  discoveredPhones.forEach(phone => {
    if (!callerIdData) {
      actions.push({
        label: `Lookup ${phone}`,
        description: 'Run Caller ID + Facebook reverse lookup',
        icon: '📞',
        color: 'border-teal-500/30 hover:border-teal-500/60 bg-teal-900/10',
        query: phone,
        available: true,
      })
    }
  })

  discoveredDomains.forEach(domain => {
    if (!domain.includes('gmail.com') && !domain.includes('yahoo.com') && !domain.includes('hotmail.com') && !domain.includes('outlook.com')) {
      actions.push({
        label: `Scan ${domain}`,
        description: 'Run LeakIX infrastructure scan on domain',
        icon: '🌐',
        color: 'border-cyan-500/30 hover:border-cyan-500/60 bg-cyan-900/10',
        query: domain,
        available: true,
      })
    }
  })

  discoveredNames.forEach(name => {
    if (name !== query) {
      actions.push({
        label: `Search "${name.length > 20 ? name.slice(0, 18) + '...' : name}"`,
        description: 'Search OCCRP Aleph + breach databases for this name',
        icon: '👤',
        color: 'border-purple-500/30 hover:border-purple-500/60 bg-purple-900/10',
        query: name,
        available: true,
      })
    }
  })

  if (actions.length === 0) return null

  // Limit to 6 most relevant
  const displayActions = actions.slice(0, 6)

  return (
    <div className="border border-gray-700/50 rounded-lg bg-black/30 p-4">
      <div className="flex items-center gap-2 mb-3">
        <span className="text-lg">🔗</span>
        <h3 className="text-gray-300 font-mono text-sm font-semibold tracking-wider">FOLLOW-UP INVESTIGATIONS</h3>
        <span className="text-gray-500 text-xs font-mono">
          {displayActions.length} discovered {displayActions.length === 1 ? 'lead' : 'leads'}
        </span>
      </div>
      <p className="text-gray-500 text-xs mb-3">
        These entities were discovered during the investigation. Click to run a new search.
      </p>
      <div className="grid grid-cols-1 gap-2">
        {displayActions.map((action, i) => (
          <motion.button
            key={`${action.query}-${i}`}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => {
              if (onRerunSearch) {
                onRerunSearch(action.query, false)
              } else {
                window.location.href = `/app/breach?query=${encodeURIComponent(action.query)}`
              }
            }}
            className={`text-left p-3 rounded-lg border ${action.color} transition-all`}
          >
            <div className="flex items-start gap-2">
              <span className="text-base mt-0.5">{action.icon}</span>
              <div className="min-w-0">
                <div className="text-white text-sm font-medium truncate">{action.label}</div>
                <div className="text-gray-500 text-xs mt-0.5">{action.description}</div>
              </div>
            </div>
          </motion.button>
        ))}
      </div>
    </div>
  )
}
